import * as React from "react";
import {
    Button,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from "@material-ui/core";
import { API } from "aws-amplify";
import { LoadingSpinner } from "../../../utils/LoadingSpinner";
import {useEffect, useState} from "react";
import { useCommonStyles } from './styles';

enum HubsProgram {
    ASSEMBIA = "ASSEMBIA",
    ASSISTRX = "ASSISTRX",
    EVERSANA = "EVERSANA"
}

const CreateHubsPrescriptionSection: React.FC = () => {
    const classes = useCommonStyles();
    const [program, setProgram] = useState<HubsProgram | ''>('');
    const [putPrescriptionRequest, setPutPrescriptionRequest] = useState('');
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [resultSuccessful, setResultSuccessful] = useState(false);
    const [response, setResponse] = useState('');

    useEffect(() => {
        setPutPrescriptionRequest(getSampleJSON());
    }, []);

    const getSampleJSON = (): string => {
        return JSON.stringify({
            "partnerPrescriptionId": "",
            "partnerPatientId": "",
            "prescriber": {
                "primaryTelephone": "",
                "npi": "",
                "deaNumber": "",
                "prescriberName": {
                    "firstName": "",
                    "lastName": "",
                    "middleName": null,
                    "suffix": null,
                    "prefix": null
                },
                "prescriberAddress": {
                    "addressLine1": "",
                    "city": "",
                    "state": "",
                    "zipCode": "",
                    "countryCode": "US"
                },
                "stateLicenseNumber": "",
                "businessName": "",
                "fax": null
            },
            "medicationPrescribed": {
                "drugDescription": "",
                "quantity": {
                    "value": "",
                    "unitOfMeasureCode": "",
                    "quantityUnitOfMeasureCode": "",
                    "codeListQualifier": ""
                },
                "strength": {
                    "value": "",
                    "strengthFormCode": "",
                    "strengthUnitOfMeasureCode": ""
                },
                "daysSupply": "",
                "writtenDate": "",
                "numberOfRefills": "",
                "directions": "",
                "ndc": "",
                "dispenseAsWritten": "",
                "effectiveDate": "",
                "firstFillDate": "",
                "lastFillDate": ""
            },
            "prescriptionTransferInDetails": {
                "sendingPharmacy": {
                    "ncpdpId": "",
                    "npi": "",
                    "pharmacyName": "",
                    "deaNumber": "",
                    "pharmacistName": {
                        "firstName": "",
                        "lastName": ""
                    },
                    "pharmacyAddress": {
                        "addressLine1": "",
                        "city": "",
                        "state": "",
                        "zipCode": "",
                        "countryCode": "US"
                    },
                    "primaryTelephone": "",
                    "fax": ""
                },
                "prescriptionTransferred": {
                    "prescriptionFillTransferInType": "",
                    "refillsTransferred": "",
                    "refillsRemaining": "",
                    "quantityTransferred": {
                        "value": "",
                        "unitOfMeasureCode": "",
                        "quantityUnitOfMeasureCode": "",
                        "codeListQualifier": ""
                    },
                    "quantityRemaining": {
                        "value": "",
                        "unitOfMeasureCode": "",
                        "quantityUnitOfMeasureCode": "",
                        "codeListQualifier": ""
                    },
                    "pharmacyRxNumber": ""
                }
            },
            "receivingPharmacy": {
                "ncpdpId": "",
                "npi": "",
                "pharmacyName": "",
                "deaNumber": null,
                "pharmacistName": {
                    "firstName": "",
                    "lastName": "",
                    "middleName": null,
                    "suffix": null,
                    "prefix": null
                },
                "pharmacyAddress": {
                    "addressLine1": "",
                    "addressLine2": null,
                    "city": "",
                    "state": "",
                    "zipCode": "",
                    "countryCode": "US"
                },
                "primaryTelephone": "",
                "fax": null
            }
        }, null, 2);
    };

    const handleValidation = (): boolean => {
        if (!program) {
            alert("Please select a program");
            return false;
        }
        if (!putPrescriptionRequest) {
            alert("Please enter a valid prescription request JSON");
            return false;
        }
        try {
            JSON.parse(putPrescriptionRequest);
        } catch (e) {
            alert("Invalid JSON format");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (handleValidation()) {
            setFormSubmitted(true);

            const apiName = "ApaqPortalAPI";
            const path = "/hubs/prescription/create";

            const requestBody = {
                program,
                putPrescriptionRequest
            };

            console.log("Selected Program:", program);
            console.log("Full Request Body:", requestBody);

            try {
                const response = await API.post(apiName, path, { body: requestBody });
                console.log("Success response:", response);
                setResultSuccessful(true);
                setResponse(JSON.stringify(response, null, 2));
            } catch (error) {
                console.error("Error details:", error);
                alert(`Error creating prescription: ${error.response?.data?.message || error.message || "Unknown error"}`);
            } finally {
                setFormSubmitted(false);
            }
        }
    };

    return (
        <div className={classes.root}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <h1>Create HUBS Prescription</h1>
                </Grid>

                <Grid item xs={12}>
                    <FormControl className={classes.formControl}>
                        <InputLabel>Program *</InputLabel>
                        <Select
                            value={program}
                            onChange={(e) => setProgram(e.target.value as HubsProgram)}
                            required
                        >
                            {Object.values(HubsProgram).map((prog) => (
                                <MenuItem key={prog} value={prog}>
                                    {prog}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Put Prescription Request (JSON) *"
                        multiline
                        rows={50}
                        value={putPrescriptionRequest}
                        onChange={(e) => setPutPrescriptionRequest(e.target.value)}
                        variant="outlined"
                        className={classes.textField}
                        required
                        placeholder='Enter JSON for putPrescriptionRequest'
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="outlined"
                        color="default"
                        onClick={handleSubmit}
                    >
                        Create Prescription
                    </Button>
                </Grid>

                {formSubmitted && (
                    <Grid item xs={12}>
                        <LoadingSpinner />
                    </Grid>
                )}

                {resultSuccessful && response && (
                    <Grid item xs={12}>
                        <TextField
                            label="Response"
                            multiline
                            rows={20}
                            value={response}
                            variant="outlined"
                            className={classes.textField}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                )}
            </Grid>
        </div>
    );
};

export default CreateHubsPrescriptionSection;
